import React, { useState, useCallback } from 'react';
import config from './config';

// Basic Loading Spinner component
export function LoadingSpinner() {
    return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
}

// Global CDS Token Handling
let cachedCDSToken = null;
let cachedCDSTokenExpiry = 0;
let tokenPromise = null;

/**
 * Fetches a new CDS token from the API
 * @returns {Promise<Object>} The CDS token and its expiry
 */
export async function fetchNewCDSToken() {
    const authToken = localStorage.getItem("token");
    if (!authToken) {
        console.error("No auth token found in localStorage");
        throw new Error("No auth token available");
    }
    // Request a new CDS token from your API endpoint
    const response = await fetch("/api/user/cds_token", {
        headers: { Authorization: `Bearer ${authToken}` }
    });
    if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch CDS token:", errorText);
        throw new Error("Failed to fetch CDS token");
    }
    const tokenData = await response.json();
    // Expect the response to contain cds_token and expires_at
    if (!tokenData.cds_token || !tokenData.expires_at) {
        throw new Error("CDS token response is missing necessary fields");
    }
    // Parse the expires_at timestamp into milliseconds
    const expiryTime = Date.parse(tokenData.expires_at);
    if (isNaN(expiryTime)) {
        throw new Error("Could not parse token expiry");
    }
    cachedCDSTokenExpiry = expiryTime;
    return tokenData;
}

/**
 * Gets the current valid token or fetches a new one if needed
 * @returns {Promise<Object>} A valid CDS token
 */
export async function getCurrentCDSToken() {
    if (cachedCDSToken && (cachedCDSTokenExpiry - Date.now() > 60000)) {
        return cachedCDSToken;
    }
    // If a token request is already in progress, wait for it.
    if (!tokenPromise) {
        tokenPromise = fetchNewCDSToken();
    }
    try {
        const token = await tokenPromise;
        cachedCDSToken = token;
        return token;
    } finally {
        tokenPromise = null;
    }
}

/**
 * Custom hook for using CDS tokens in components
 */
export function useCDSToken() {
    const [cdsToken, setCDSToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchCDSToken = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.log('No auth token found in localStorage');
            return null;
        }

        try {
            setLoading(true);
            setError(null);
            console.log('Fetching CDS token...');

            const response = await fetch("/api/user/cds_token", {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            console.log('CDS token response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.log('CDS token error response:', errorText);
                throw new Error('Failed to fetch CDS token');
            }

            const data = await response.json();
            console.log('Got CDS token response:', data);

            if (!data.cds_token) {
                throw new Error('No CDS token in response');
            }

            setCDSToken(data);
            return data;
        } catch (err) {
            console.error('Error fetching CDS token:', err);
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { cdsToken, loading, error, fetchCDSToken };
}